import axios from 'axios';
import store from 'store';
import jwtDecode from 'jwt-decode';

/**
 * Can be used with API libraries to handle actual requests
 */
class DataService {
  /**
   * @param {Object} options - option object
   * @param {string} authStoreKey - key name of authorization token, e.g. 'api-token'
   */
  constructor(options = {}) {
    let settings = {
      authStoreKey: null,
      baseUrl: null
    };

    this.settings = Object.assign(settings, options);

    if(!this.settings.authStoreKey) {
      throw Error('#constructor: settings.authStoreKey needs to be set');
    }

    if(!this.settings.baseUrl) {
      throw Error('#constructor: settings.baseUrl needs to be set');
    }

    const params = this.settings.requestParams || {};

    this.ax = axios.create({
      baseURL: this.settings.baseUrl,
      headers: {
        'X-Requested-with': 'XHR'
      },
      params,
      validateStatus: function (status) {
        return status < 500; // Reject only if the status code is greater than or equal to 500
      }
    });


    const token = store.get(this.settings.authStoreKey);
    if(token) {
      this.setAuthToken(token);
    }
  }

  async request(method, options) {
    return this.ax({
      method: method,
      url: options.url,
      data: options.data,
      params: options.params || {},
      withCredentials: true,
      config: options.config
    }).then(res => {
      const data = res.data;

      if(!this.vuexStore) {
        return data;
      }

      if(data.code === 401) {
        // Clear auth token
        this.clearAuthToken();
        this.vuexStore.commit('setLoginStatus', false);
      }

      // If ok or too many requests
      if((data.code === 200 || data.code === 429) && data.messages.length) {
        this.vuexStore.commit('addNotification', data.messages);
      }

      return data;

    }).catch(function (error) {
      if(error.response) {
        const data = error.response.data;
        console.error(`Server error status: ${data.code}\n${data.result.error.stack}`);
      }
    });
  }

  /**
	 * If you're using dataService this with vuex, we can automatically logout user and do other stuff based on responses
	 *
	 * @param {Object} store - vuex store object
	 */
  setVuexStore(store) {
    this.vuexStore = store;
  }

  get(path, params) {
    return this.request('get', {
      url: path,
      params: params
    });
  }

  post(path, data, params, config) {
    return this.request('post', {
      url: path,
      data: data,
      params: params,
      config: config
    });
  }

  postForm(path, data, params, config) {
    const paramsString = this.getParamsString(data);
    return this.request('post', {
      url: path,
      data: paramsString,
      params: params,
      config: { headers: {'Content-Type': 'application/x-www-form-urlencoded' }}
    });
  }

  patch(path, data, params) {
    return this.request('patch', {
      url: path,
      data: data,
      params: params
    });
  }

  put(path, data, params) {
    return this.request('put', {
      url: path,
      data: data,
      params: params
    });
  }

  delete(path, params) {
    return this.request('delete', {
      url: path,
      params: params
    });
  }

  setAuthToken(token) {
    this.ax.defaults.headers.common['Authorization'] = 'Bearer ' + token;
    store.set(this.settings.authStoreKey, token);
  }

  clearAuthToken() {
    delete this.ax.defaults.headers.common['Authorization'];
    store.remove(this.settings.authStoreKey);
  }

  getAuthToken() {
    return store.get(this.settings.authStoreKey);
  }

  isLoggedIn() {
    // Checks if there is a saved token and it's still valid
    const token = this.getAuthToken(); // GEtting token from localstorage
    return !!token && !this.isAuthTokenExpired(token) // handwaiving here
  }

  isAuthTokenExpired(token) {
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp < Date.now() / 1000) { // Checking if token is expired.
        return true;
      }

      return false;
    } catch (err) {
      return false;
    }
  }

  getParamsString(params, baseUrl) {
    let encodedString = /\?/g.test(baseUrl) ? '&' : '?';

    // If baseUrl is omitted, this is params request for POST or PUT type xhr, se we can only return params
    if (!baseUrl) {
      encodedString = '';
    }

    if (!params) {
      return '';
    }

    if (typeof params === 'string') {
      return params;
    }

    for (let prop in params) {
      if (Object.prototype.hasOwnProperty.call(params, prop)) {
        // if array
        if (typeof params[prop] === 'object' && params[prop].length) {
          for (let i = 0, l = params[prop].length; i < l; i++) {
            if (encodedString.length > 1) {
              encodedString += '&';
            }

            encodedString += encodeURIComponent(prop + '[]') + '=' + encodeURIComponent(params[prop][i]);
          }
        } else {
          if (encodedString.length > 1) {
            encodedString += '&';
          }
          encodedString += encodeURIComponent(prop) + '=' + encodeURIComponent(params[prop]);
        }
      }
    }

    return encodedString;
  }
}

export default DataService;
